var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _verbose, _sendChunks, sendChunks_fn, _handleBlocksProtocol, handleBlocksProtocol_fn, _log, log_fn;
import sha256 from "fast-sha256";
var LedgerToHost = /* @__PURE__ */ ((LedgerToHost2) => {
  LedgerToHost2[LedgerToHost2["RESULT_ACCUMULATING"] = 0] = "RESULT_ACCUMULATING";
  LedgerToHost2[LedgerToHost2["RESULT_FINAL"] = 1] = "RESULT_FINAL";
  LedgerToHost2[LedgerToHost2["GET_CHUNK"] = 2] = "GET_CHUNK";
  LedgerToHost2[LedgerToHost2["PUT_CHUNK"] = 3] = "PUT_CHUNK";
  return LedgerToHost2;
})(LedgerToHost || {});
var HostToLedger = /* @__PURE__ */ ((HostToLedger2) => {
  HostToLedger2[HostToLedger2["START"] = 0] = "START";
  HostToLedger2[HostToLedger2["GET_CHUNK_RESPONSE_SUCCESS"] = 1] = "GET_CHUNK_RESPONSE_SUCCESS";
  HostToLedger2[HostToLedger2["GET_CHUNK_RESPONSE_FAILURE"] = 2] = "GET_CHUNK_RESPONSE_FAILURE";
  HostToLedger2[HostToLedger2["PUT_CHUNK_RESPONSE"] = 3] = "PUT_CHUNK_RESPONSE";
  HostToLedger2[HostToLedger2["RESULT_ACCUMULATING_RESPONSE"] = 4] = "RESULT_ACCUMULATING_RESPONSE";
  return HostToLedger2;
})(HostToLedger || {});
class Sui {
  constructor(transport, scrambleKey = "default_sui_scramble_key", verbose = false) {
    /**
     * Convert a raw payload into what is essentially a singly-linked list of chunks, which
     * allows the ledger to re-seek the data in a secure fashion.
     */
    __privateAdd(this, _sendChunks);
    __privateAdd(this, _handleBlocksProtocol);
    __privateAdd(this, _log);
    __privateAdd(this, _verbose, void 0);
    __privateSet(this, _verbose, verbose);
    this.transport = transport;
    this.transport.decorateAppAPIMethods(
      this,
      ["getPublicKey", "signTransaction", "getVersion"],
      scrambleKey
    );
  }
  /**
   * Retrieves the public key associated with a particular BIP32 path from the Ledger app.
   *
   * @param path - the path to retrieve.
   * @param displayOnDevice - whether or not the address should be displayed on the device.
   *
   */
  async getPublicKey(path, displayOnDevice = false) {
    const cla = 0;
    const ins = displayOnDevice ? 1 : 2;
    const p1 = 0;
    const p2 = 0;
    const payload = buildBip32KeyPayload(path);
    const response = await __privateMethod(this, _sendChunks, sendChunks_fn).call(this, cla, ins, p1, p2, payload);
    const keySize = response[0];
    const publicKey = response.slice(1, keySize + 1);
    let address = null;
    if (response.length > keySize + 2) {
      const addressSize = response[keySize + 1];
      address = response.slice(keySize + 2, keySize + 2 + addressSize);
    }
    const res = {
      publicKey,
      address
    };
    return res;
  }
  /**
   * Sign a transaction with the key at a BIP32 path.
   *
   * @param txn - The transaction; this can be any of a node Buffer, Uint8Array, or a hexadecimal string, encoding the form of the transaction appropriate for hashing and signing.
   * @param path - the path to use when signing the transaction.
   */
  async signTransaction(path, txn) {
    const cla = 0;
    const ins = 3;
    const p1 = 0;
    const p2 = 0;
    if (__privateGet(this, _verbose))
      __privateMethod(this, _log, log_fn).call(this, txn);
    const rawTxn = typeof txn == "string" ? Buffer.from(txn, "hex") : Buffer.from(txn);
    const hashSize = Buffer.alloc(4);
    hashSize.writeUInt32LE(rawTxn.length, 0);
    const bip32KeyPayload = buildBip32KeyPayload(path);
    const payload_txn = Buffer.concat([hashSize, rawTxn]);
    __privateMethod(this, _log, log_fn).call(this, "Payload Txn", payload_txn);
    const signature = await __privateMethod(this, _sendChunks, sendChunks_fn).call(this, cla, ins, p1, p2, [payload_txn, bip32KeyPayload]);
    return {
      signature
    };
  }
  /**
   * Retrieve the app version on the attached Ledger device.
   */
  async getVersion() {
    const [major, minor, patch] = await __privateMethod(this, _sendChunks, sendChunks_fn).call(this, 0, 0, 0, 0, Buffer.alloc(1));
    return {
      major,
      minor,
      patch
    };
  }
}
_verbose = new WeakMap();
_sendChunks = new WeakSet();
sendChunks_fn = async function(cla, ins, p1, p2, payload, extraData = /* @__PURE__ */ new Map()) {
  let chunkSize = 180;
  if (!(payload instanceof Array)) {
    payload = [payload];
  }
  let parameterList = [];
  let data = new Map(extraData);
  for (let j = 0; j < payload.length; j++) {
    let chunkList = [];
    for (let i = 0; i < payload[j].length; i += chunkSize) {
      let cur = payload[j].slice(i, i + chunkSize);
      chunkList.push(cur);
    }
    let lastHash = Buffer.alloc(32);
    __privateMethod(this, _log, log_fn).call(this, lastHash);
    data = chunkList.reduceRight((blocks, chunk) => {
      let linkedChunk = Buffer.concat([lastHash, chunk]);
      __privateMethod(this, _log, log_fn).call(this, "Chunk: ", chunk);
      __privateMethod(this, _log, log_fn).call(this, "linkedChunk: ", linkedChunk);
      lastHash = Buffer.from(sha256(linkedChunk));
      blocks.set(lastHash.toString("hex"), linkedChunk);
      return blocks;
    }, data);
    parameterList.push(lastHash);
    lastHash = Buffer.alloc(32);
  }
  __privateMethod(this, _log, log_fn).call(this, data);
  return await __privateMethod(this, _handleBlocksProtocol, handleBlocksProtocol_fn).call(this, cla, ins, p1, p2, Buffer.concat([Buffer.from([0 /* START */])].concat(parameterList)), data);
};
_handleBlocksProtocol = new WeakSet();
handleBlocksProtocol_fn = async function(cla, ins, p1, p2, initialPayload, data) {
  let payload = initialPayload;
  let result = Buffer.alloc(0);
  do {
    __privateMethod(this, _log, log_fn).call(this, "Sending payload to ledger: ", payload.toString("hex"));
    let rv = await this.transport.send(cla, ins, p1, p2, payload);
    __privateMethod(this, _log, log_fn).call(this, "Received response: ", rv);
    var rv_instruction = rv[0];
    let rv_payload = rv.slice(1, rv.length - 2);
    if (!(rv_instruction in LedgerToHost)) {
      throw new TypeError("Unknown instruction returned from ledger");
    }
    switch (rv_instruction) {
      case 0 /* RESULT_ACCUMULATING */:
      case 1 /* RESULT_FINAL */:
        result = Buffer.concat([result, rv_payload]);
        payload = Buffer.from([4 /* RESULT_ACCUMULATING_RESPONSE */]);
        break;
      case 2 /* GET_CHUNK */:
        let chunk = data.get(rv_payload.toString("hex"));
        __privateMethod(this, _log, log_fn).call(this, "Getting block ", rv_payload);
        __privateMethod(this, _log, log_fn).call(this, "Found block ", chunk);
        if (chunk) {
          payload = Buffer.concat([
            Buffer.from([1 /* GET_CHUNK_RESPONSE_SUCCESS */]),
            chunk
          ]);
        } else {
          payload = Buffer.from([2 /* GET_CHUNK_RESPONSE_FAILURE */]);
        }
        break;
      case 3 /* PUT_CHUNK */:
        data.set(Buffer.from(sha256(rv_payload)).toString("hex"), rv_payload);
        payload = Buffer.from([3 /* PUT_CHUNK_RESPONSE */]);
        break;
    }
  } while (rv_instruction !== 1 /* RESULT_FINAL */);
  return result;
};
_log = new WeakSet();
log_fn = function(...args) {
  if (__privateGet(this, _verbose))
    console.log(args);
};
function buildBip32KeyPayload(path) {
  const paths = splitPath(path);
  const payload = Buffer.alloc(1 + paths.length * 4);
  payload[0] = paths.length;
  paths.forEach((element, index) => {
    payload.writeUInt32LE(element, 1 + 4 * index);
  });
  return payload;
}
function splitPath(path) {
  const result = [];
  const components = path.split("/");
  components.forEach((element) => {
    let number = parseInt(element, 10);
    if (isNaN(number)) {
      return;
    }
    if (element.length > 1 && element[element.length - 1] === "'") {
      number += 2147483648;
    }
    result.push(number);
  });
  return result;
}
export {
  Sui as default
};
//# sourceMappingURL=Sui.js.map
